import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { BACKEND_ORIGIN } from '@/config/api';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) throw new Error('useSocket must be used within SocketProvider');
  return context;
};

const emitWithAck = (socket, event, payload) => new Promise((resolve, reject) => {
  if (!socket?.connected) {
    reject(new Error('Socket is disconnected'));
    return;
  }
  socket.timeout(8000).emit(event, payload, (error, response) => {
    if (error) {
      reject(new Error('Realtime request timed out'));
      return;
    }
    if (!response?.ok) {
      reject(new Error(response?.error?.message || 'Realtime request failed'));
      return;
    }
    resolve(response);
  });
});

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const conversationRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);

  const connectSocket = useCallback((token) => {
    if (!token) return () => {};
    if (socketRef.current?.connected) return () => socketRef.current?.disconnect();
    socketRef.current?.disconnect();
    const nextSocket = io(BACKEND_ORIGIN, {
      path: '/api/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });
    nextSocket.on('connect', () => {
      setConnected(true);
      if (conversationRef.current) {
        nextSocket.emit('join_conversation', { conversation_id: conversationRef.current });
      }
    });
    nextSocket.on('disconnect', () => setConnected(false));
    socketRef.current = nextSocket;
    setSocket(nextSocket);
    return () => {
      if (socketRef.current === nextSocket) socketRef.current = null;
      nextSocket.disconnect();
    };
  }, []);

  const disconnectSocket = useCallback(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    setSocket(null);
    setConnected(false);
    setCurrentConversation(null);
    conversationRef.current = null;
  }, []);

  const joinConversation = useCallback(async (conversationId) => {
    const result = await emitWithAck(socketRef.current, 'join_conversation', { conversation_id: conversationId });
    conversationRef.current = conversationId;
    setCurrentConversation(conversationId);
    return result;
  }, []);

  const leaveConversation = useCallback(async (conversationId) => {
    if (!socketRef.current?.connected) {
      setCurrentConversation(null);
      conversationRef.current = null;
      return { ok: true };
    }
    const result = await emitWithAck(socketRef.current, 'leave_conversation', { conversation_id: conversationId });
    setCurrentConversation((current) => current === conversationId ? null : current);
    if (conversationRef.current === conversationId) conversationRef.current = null;
    return result;
  }, []);

  const sendMessage = useCallback((payload) => emitWithAck(socketRef.current, 'send_message', payload), []);
  const sendTyping = useCallback((conversationId, isTyping = true) => (
    emitWithAck(socketRef.current, 'typing', { conversation_id: conversationId, is_typing: isTyping })
  ), []);
  const markAsRead = useCallback((conversationId) => (
    emitWithAck(socketRef.current, 'mark_read', { conversation_id: conversationId })
  ), []);
  const sendCallEvent = useCallback((event, payload) => emitWithAck(socketRef.current, event, payload), []);

  useEffect(() => () => socketRef.current?.disconnect(), []);

  return (
    <SocketContext.Provider value={{
      socket,
      connected,
      currentConversation,
      connectSocket,
      disconnectSocket,
      joinConversation,
      leaveConversation,
      sendMessage,
      sendTyping,
      markAsRead,
      sendCallEvent,
    }}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
